import React, { useCallback } from 'react';
import { Button, Input, Label } from 'reactstrap';
import { isNumber, Storage, translate, Translate, ValidatedField } from 'react-jhipster';
import Draggable from 'react-draggable';
import { useForm } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

interface CommissionDialogProps {
  open: boolean;
  isNew: boolean;
  eachAmountValue: string;
  noDecimalAllowed: boolean;
  loading: boolean;
  updating: boolean;
  feeForm: React.ReactNode;
  onClose: () => void;
  onSubmit: (values: any) => void;
  onEachAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  commodityType: 'International' | 'Local';
}

function toEnglishDigits(str: string): string {
  let e = '۰'.charCodeAt(0);
  str = str.replace(/[۰-۹]/g, function (t) {
    return String(t.charCodeAt(0) - e);
  });

  e = '٠'.charCodeAt(0);
  str = str.replace(/[٠-٩]/g, function (t) {
    return String(t.charCodeAt(0) - e);
  });
  return str;
}

export const BankDetailCommissionDialog: React.FC<CommissionDialogProps> = React.memo(
  ({
    open,
    isNew,
    eachAmountValue,
    noDecimalAllowed,
    loading,
    updating,
    feeForm,
    onClose,
    onSubmit,
    onEachAmountChange,
    commodityType,
  }) => {
    const {
      handleSubmit,
      register,
      formState: { errors },
    } = useForm({ mode: 'onTouched' });

    if (loading || updating) {
      return (
        <Dialog open={open} onClose={onClose}>
          <DialogTitle style={{ fontWeight: 'regular' }}>Loading</DialogTitle>
        </Dialog>
      );
    }

    return (
      <Draggable handle=".dialog-title-handle">
        <Dialog
          open={open}
          onClose={onClose}
          hideBackdrop={true}
          maxWidth="sm"
          fullWidth={true}
          sx={Storage.session.get('locale') === 'ar-ly' ? { marginLeft: '40px' } : null}
        >
          <DialogTitle className="dialog-title-handle" style={{ fontWeight: 'bold', cursor: 'move' }}>
            <Grid container spacing={2}>
              <Grid item xs={10} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                {isNew ? (
                  <Translate contentKey="comexApp.bank.details.addNewFeeRange" />
                ) : (
                  <Translate contentKey="comexApp.bank.details.updateFeeRange" />
                )}
              </Grid>
            </Grid>
          </DialogTitle>
          <form
            onSubmit={e => {
              e.preventDefault();
              onSubmit({});
            }}
          >
            <DialogContent>
              <Grid sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    position: 'absolute',
                    top: 10,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Grid>
              <DialogContentText style={{ color: 'black' }} className="mb-3" component={'span'}>
                {!isNew && (
                  <ValidatedField
                    name="id"
                    register={register}
                    hidden
                    readOnly
                    id={`comex-bank-fee-id-commission-${commodityType.toLowerCase()}`}
                    label={translate('global.field.id')}
                    validate={{ required: true }}
                  />
                )}
                <ValidatedField
                  label={translate('comexApp.bank.details.table.commissionForEachAmount')}
                  id={`comex-bank-fee-fromTo-commission-${commodityType.toLowerCase()}`}
                  error={errors?.fromTo}
                  name="commissionEachAmount"
                  data-cy="commissionEachAmount"
                  type="text"
                  register={register}
                  onChange={onEachAmountChange}
                  value={eachAmountValue}
                />
                <div className="bank-fee-no-decimal-error" hidden={noDecimalAllowed}>
                  {translate('comexApp.bank.validation.noDecimalAllowed')}
                </div>

                <br />
                <div>
                  <Label>{translate('comexApp.bank.details.table.commissionAmount')}</Label>
                  {feeForm}
                </div>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }} className="mx-3 mb-3">
              <Button onClick={onClose}>
                <Translate contentKey="comexApp.bank.details.cancel" />
              </Button>
              <Button
                color="primary"
                type="submit"
                style={Storage.session.get('locale') === 'en' ? { marginLeft: '10px' } : { marginRight: '10px' }}
                disabled={updating}
              >
                {isNew ? <Translate contentKey="comexApp.bank.details.add" /> : <Translate contentKey="comexApp.bank.details.update" />}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Draggable>
    );
  }
);

BankDetailCommissionDialog.displayName = 'BankDetailCommissionDialog';
